import React, { useState, useMemo, type ReactNode } from 'react'

import {
  EditOutlined,
  ScissorOutlined,
  RollbackOutlined,
  FileAddOutlined,
  SwapOutlined,
  ThunderboltOutlined,
  ReloadOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
  ExportOutlined,
  FilePdfOutlined,
  LinkOutlined,
  QuestionCircleOutlined,
  InfoCircleOutlined,
  StopOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { useMerchantData } from '@contexts/admin/MerchantDataContext'
import { useOperatorData } from '@contexts/operator/OperatorDataContext'
import useBatchOperation from '@hooks/shared/useBatchOperation'
import { useImport } from '@hooks/shared/useImport'
import ImportModal from '@shared/ImportModal'
import ConfirmModal from '@shared/ConfirmModal'
import OrderBatchPanel from '@shared/table/OrderBatchPanel'
import { MultipleValue, BooleanCheckbox, NumberRange, SearchValue } from '@shared/table/Rules/component'
import FullDayRangePicker from '@components/shared/FullDayRangePicker'
import TableIndex from '@shared/table/TableIndex'
import type { TableAction, TableService, SupportRule } from '@shared/table/TableIndex'
import OrderStatusLabel from '@shared/orders/OrderStatusLabel'
import HoldOrderModal from '@shared/orders/HoldOrderModal'
import useCan from '@shared/permissions/useCan'
import type { Permission } from '@/permissions_manifest.generated'
import { Select } from '@shared/ui'
import { Modal, Button, Space, Tooltip, Tag, theme } from 'antd'
import { useTranslation } from 'react-i18next'
import { formatDateTime } from '@utils/dateTime'
import { Link } from 'react-router-dom'
import { downloadImportTemplate } from '@utils/downloadTemplate'
import { SHIPMENT_STATUS_COLORS } from '@shared/shipments/statusColors'
import { useOrderStatusLabel } from '@constants/order'
import { SHIPMENT_STATUSES, useShipmentStatusLabel } from '@constants/shipment'

// 訂單狀態篩選下拉選項，依 Order AASM 合法狀態 (11 個) 與業務流程順序排列
// AASM source of truth: app/models/order.rb
export const ORDER_STATUS_FILTER_OPTIONS = [
  'pending',
  'awaiting_payment',
  'allocated',
  'partial_allocated',
  'out_of_stock',
  'processing',
  'partial_shipped',
  'shipped',
  'canceled',
  'draft',
] as const

// OrderList 直接呼叫多個 service method（batchUpdate / batchCheck / export / ...）
// 沿用 TableService 為基底加 `[key: string]: any`，呼叫端 service 物件型別保持寬鬆。
interface OrderListService extends TableService {
  [key: string]: any;
}

interface MerchantOption {
  id: string;
  name: string;
  [key: string]: unknown;
}

interface ShippingTypeOption {
  id: string;
  code: string;
  name: string;
}

interface ChannelOption {
  label: string;
  value: string;
}

interface OrderRow {
  id: string;
  [key: string]: unknown;
}

interface BatchEditModalProps {
  open: boolean;
  onClose: () => void;
  selected: Iterable<string> | Set<string> | string[];
  onTaskDone?: () => void;
  channelOptions?: ChannelOption[];
  shippingTypes: ShippingTypeOption[];
  service: OrderListService;
  addJob?: (jobId: string, jobName: string) => void;
}

const BatchEditModal: React.FC<BatchEditModalProps> = ({ open, onClose, selected, onTaskDone, channelOptions = [], shippingTypes, service, addJob }) => {
  const { t } = useTranslation()
  const [editType, setEditType] = useState('')
  const [editData, setEditData] = useState<Record<string, unknown>>({})

  const executeBatchOperation = useBatchOperation(service, addJob)

  const editTypeOptions = useMemo(() => ([
    {
      label: t(`shared.orders.list.batch_edit.options.shipping_type`),
      value: 'shipping_type',
    },
    {
      label: t(`shared.orders.list.batch_edit.options.channel`),
      value: 'sales_channel_id',
    }
  ]), [t])

  const shippingTypeOptions = useMemo(() => {
    return shippingTypes.map(item => ({
      label: item.name,
      value: item.id,
    }))
  }, [shippingTypes])


  const handleUpdate = () => {
    executeBatchOperation({
      task: service ? () => service.batchUpdate(Array.from(selected as Iterable<string>), editData) : undefined,
      url: 'orders/batch_update.json',
      selected,
      params: { actions: editData },
      jobName: 'shared.orders.list.batch_edit.title',
      onSuccess: () => onTaskDone && onTaskDone()
    })
    onClose()
  }

  const handleClose = () => {
    setEditType('')
    setEditData({})
    onClose()
  }

  return (
    <Modal
      title={t(`shared.orders.list.batch_edit.title`)}
      centered
      open={open}
      onCancel={handleClose}
      width={520}
      destroyOnHidden
      footer={[
        <Button key="cancel" onClick={handleClose}>
          {t('common.button.cancel')}
        </Button>,
        <Button key="submit" type="primary" onClick={handleUpdate} disabled={!editType || Object.keys(editData).length === 0}>
          {t(`common.button.confirm`)}
        </Button>
      ]}
    >
      <div style={{ padding: '8px 0' }}>
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
            {t(`shared.orders.list.batch_edit.edit_type_label`)}
          </label>
          <Select
            options={editTypeOptions}
            value={editTypeOptions.find((option) => option.value === editType) || null}
            onChange={(option: { value: string } | null) => {
              setEditType(option ? option.value : '')
              setEditData({})
            }}
            placeholder={t('common.select.please_select')}
          />
        </div>

        {editType === 'shipping_type' && (
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
              {t(`shared.orders.list.batch_edit.shipping_type_label`)}
            </label>
            <Select
              options={shippingTypeOptions}
              value={shippingTypeOptions.find((option) => option.value === editData.shipping_type) || null}
              onChange={(option: { value: string } | null) => {
                setEditData({
                  shipping_type: option ? option.value : undefined
                })
              }}
              placeholder={t('common.select.please_select')}
            />
          </div>
        )}

        {editType === 'sales_channel_id' && (
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
              {t(`shared.orders.list.batch_edit.channel_label`)}
            </label>
            <Select
              options={channelOptions}
              value={channelOptions.find((option) => option.value === editData.sales_channel_id) || null}
              onChange={(option: { value: string } | null) => {
                setEditData({
                  sales_channel_id: option ? option.value : undefined
                })
              }}
              placeholder={t('common.select.please_select')}
            />
          </div>
        )}
      </div>
    </Modal>
  )
}

interface ConvertOrderToDraftOrderModalProps {
  open: boolean;
  onClose: () => void;
  selected: Iterable<string> | Set<string> | string[];
  onTaskDone?: () => void;
  service: OrderListService;
  addJob?: (jobId: string, jobName: string) => void;
  role?: string;
}

const ConvertOrderToDraftOrderModal: React.FC<ConvertOrderToDraftOrderModalProps> = ({ open, onClose, selected, onTaskDone, service, addJob }) => {
  const { t } = useTranslation()
  const [scheduledShippingDate, setScheduledShippingDate] = useState('')

  const executeBatchOperation = useBatchOperation(service, addJob)

  const handleConvert = () => {
    if (!scheduledShippingDate) {
      alert(t(`shared.orders.list.convert_to_draft.select_required`))
      return
    }

    executeBatchOperation({
      task: service ? () => service.batchConvertToDraftOrder(Array.from(selected as Iterable<string>), { scheduled_shipping_date: scheduledShippingDate }) : undefined,
      url: 'orders/batch_convert_to_draft_order.json',
      selected,
      params: { scheduled_shipping_date: scheduledShippingDate },
      jobName: 'shared.orders.list.convert_to_draft.title',
      onSuccess: () => onTaskDone && onTaskDone()
    })
    onClose()
  }

  return (
    <Modal
      title={t(`shared.orders.list.convert_to_draft.title`)}
      centered
      open={open}
      onCancel={onClose}
      width={520}
      destroyOnHidden
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t('common.button.cancel')}
        </Button>,
        <Button key="submit" type="primary" disabled={!scheduledShippingDate} onClick={handleConvert}>
          {t(`shared.orders.list.convert_to_draft.confirm`)}
        </Button>
      ]}
    >
      <div style={{ padding: '8px 0' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
          {t(`shared.orders.list.convert_to_draft.scheduled_shipping_date_label`)}
        </label>
        <input
          type="date"
          id="scheduled_shipping_date"
          required
          value={scheduledShippingDate}
          onChange={(e) => setScheduledShippingDate(e.target.value)}
          style={{
            width: '100%',
            padding: '4px 11px',
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            fontSize: '14px',
            lineHeight: '1.5715',
          }}
        />
      </div>
    </Modal>
  )
}

interface OrderListProps {
  role?: 'admin' | 'operator';
  service: OrderListService;
  addJob?: (jobId: string, jobName: string) => void;
  shippingTypes?: ShippingTypeOption[];
  merchants?: MerchantOption[];
  importModalVisible?: boolean;
  setImportModalVisible?: (visible: boolean) => void;
  hideMerchantUI?: boolean; // Operator single-merchant 模式使用
}

const OrderList: React.FC<OrderListProps> = ({
  role = 'admin',
  service,
  addJob,
  shippingTypes = [],
  merchants = [],
  importModalVisible: externalImportModalVisible,
  setImportModalVisible: externalSetImportModalVisible,
  hideMerchantUI = false // Operator single-merchant 模式使用
}) => {
  const { t, i18n } = useTranslation()
  const { token } = theme.useToken()
  const can = useCan()
  const orderStatusLabel = useOrderStatusLabel()
  const shipmentStatusLabel = useShipmentStatusLabel()

  // 根據 role 從對應的 context 獲取 salesChannels
  // Always call hooks unconditionally
  const adminData = useMerchantData()
  const operatorData = useOperatorData()
  const salesChannels = role === 'admin' ? (adminData?.salesChannels || []) : (operatorData?.salesChannels || [])

  const [lastModified, setLastModified] = useState(0)

  // 使用共用 batch operation hook
  const executeBatchOperation = useBatchOperation(service, addJob)
  const [showBatchEditPanel, setShowBatchEditPanel] = useState(false)

  // 匯入類型選項（使用與建立訂單相同的單據類別選項）
  const importTypeOptions = useMemo(() => [
    {
      label: t('enums.order_type.normal'),
      value: 'Order'
    },
  ], [t])
  // 預設匯入類型為「正式訂單」（第一個選項）
  const defaultImportType = useMemo(() => {
    return importTypeOptions.length > 0 ? importTypeOptions[0].value : 'Order'
  }, [importTypeOptions])

  // 匯入功能（使用 Context 共享背景任務管理器）
  const handleImport = useImport({
    action: (formData: FormData) => service.import(formData),
    addJob,
    jobName: 'shared.orders.list.import.job_name',
    typeParamName: 'order_type', // 訂單匯入使用 order_type 參數
    i18nKeys: {
      noFile: 'shared.orders.list.import.no_file',
      invalidFile: 'shared.orders.list.import.invalid_file',
      success: 'shared.orders.list.import.success',
      error: 'shared.orders.list.import.error',
    }
    // 注意：不在 onSuccess 重新載入表格，因為 Background Job 還在執行中
    // 使用者需要在 Background Job 完成後手動重新整理，或等待自動通知
  })

  // 處理 Modal 匯入
  const handleModalImport = async (file: File, merchantId: string, overrideExisting: boolean, importType: string, attachments?: unknown) => {
    await handleImport(file, merchantId, overrideExisting, importType, attachments as File[] | undefined)
  }

  // 處理下載範例
  const handleDownloadTemplate = async (merchantId?: string) => {
    await downloadImportTemplate({
      service,
      language: i18n.language,
      merchantId,
      t,
      successKey: 'shared.orders.list.template.success',
      errorKey: 'shared.orders.list.template.error'
    })
  }
  const [isDraftOrderModalOpen, setIsDraftOrderModalOpen] = useState(false)
  const [draftOrderModalSelected, setDraftOrderModalSelected] = useState<Iterable<string> | string[]>([])
  const [batchEditModalOpen, setBatchEditModalOpen] = useState(false)
  const [batchEditModalSelected, setBatchEditModalSelected] = useState<Iterable<string> | string[]>([])
  const [batchCancelModalOpen, setBatchCancelModalOpen] = useState(false)
  const [batchCancelSelected, setBatchCancelSelected] = useState<Set<string>>(new Set())
  const [batchHoldModalOpen, setBatchHoldModalOpen] = useState(false)
  const [batchHoldSelected, setBatchHoldSelected] = useState<Set<string>>(new Set())
  const [internalImportModalVisible, setInternalImportModalVisible] = useState(false)

  // Support external or internal import modal control
  const importModalVisible = externalImportModalVisible !== undefined ? externalImportModalVisible : internalImportModalVisible
  const setImportModalVisible = externalSetImportModalVisible || setInternalImportModalVisible

  const urlPrefix = role === 'operator' ? '/operator' : '/admin'

  const shippingTypeOptions = shippingTypes.map(shippingType => ({
    label: shippingType.name,
    value: shippingType.id,
  }))

  // 將 salesChannels 轉換為 select options 格式
  const channelOptions = useMemo(() => {
    return salesChannels.map((channel: { id: string; name: string }) => ({
      label: channel.name,
      value: channel.id,
    }))
  }, [salesChannels])


  const onCloseBatchEditPanel = () => {
    setShowBatchEditPanel(false);
  };

  const statusOptionsValues = ORDER_STATUS_FILTER_OPTIONS

  const localizedShipmentStatusOptions = useMemo(
    () => SHIPMENT_STATUSES.map(value => ({ value, label: shipmentStatusLabel(value) })),
    [shipmentStatusLabel]
  )

  const localizedStatusOptions = useMemo(() => statusOptionsValues.map(value => ({
    value,
    label: orderStatusLabel(value),
  })), [orderStatusLabel, statusOptionsValues])

  const baseColumns = useMemo<unknown[]>(() => {
    const cols: Record<string, unknown>[] = [
      {
        nameKey: `models.order.order_number`,
        persist: true,
        sortable: true,
        sortFieldName: 'orders.name',
        width: '200px',
        cell: ({ id, name, urgent }: { id: string; name: string; urgent?: boolean }) => (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {urgent && (
                <Tag color="error" style={{ marginRight: 4, fontSize: '10px', lineHeight: '16px', padding: '0 4px' }}>
                  {t(`shared.orders.list.badges.urgent`)}
                </Tag>
              )}
              <Link to={`/orders/${id}`} className={urgent ? 'fw-bold text-danger' : ''}>
                {name}
              </Link>
            </div>
            <a
              href={`${urlPrefix}/orders/${id}`}
              target="_blank"
              onClick={(e) => e.stopPropagation()}
              style={{ flexShrink: 0 }} rel="noreferrer"
            >
              <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
            </a>
          </div>
        ),
      },
      {
        nameKey: `models.order.reference_number`,
        sortable: false,
        selector: ({ extra_name }: { extra_name?: string }) => extra_name,
      }
    ];

    // Add merchant and warehouse columns for operator role
    // merchant 欄位於 single-merchant 模式下隱藏，warehouse 永遠顯示
    if (role === 'operator') {
      if (!hideMerchantUI) {
        cols.push({
          nameKey: `models.merchant.name`,
          wrap: true,
          selector: ({ merchant_id }: { merchant_id?: string }) => {
            return merchants.find((merchant) => merchant.id === merchant_id)?.name
          },
        });
      }
      cols.push({
        nameKey: `models.warehouse.name`,
        sortable: false,
        wrap: true,
        selector: ({ warehouse_name }: { warehouse_name?: string }) => warehouse_name,
      });
    }

    cols.push(
      {
        name: (
          <Space size={4}>
            {t('models.order.expected_quantity')}
            <Tooltip title={t('shared.orders.list.column_tooltips.expected_quantity')}>
              <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
            </Tooltip>
          </Space>
        ),
        sortable: false,
        selector: ({ total_quantity }: { total_quantity?: number }) => total_quantity,
      },
      {
        name: (
          <Space size={4}>
            {t('models.order.shipped_quantity')}
            <Tooltip title={t('shared.orders.list.column_tooltips.shipped_quantity')}>
              <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
            </Tooltip>
          </Space>
        ),
        sortable: false,
        selector: ({ shipped_quantity }: { shipped_quantity?: number }) => shipped_quantity,
      },
      {
        nameKey: `models.order.sales_channel`,
        sortable: false,
        selector: ({ sales_channel_name }: { sales_channel_name?: string }) => sales_channel_name,
      },
      {
        nameKey: `models.order.status`,
        sortable: false,
        width: '180px',
        cell: ({ status, is_held }: { status?: string; is_held?: boolean }) => <OrderStatusLabel status={status} is_held={is_held} />,
      },
      {
        nameKey: `models.order.receiver_name`,
        sortable: true,
        sortFieldName: 'orders.receiver_name',
        selector: ({ receiver_name }: { receiver_name?: string }) => receiver_name,
      },
      {
        nameKey: `models.order.shipping_type`,
        selector: ({ shipping_type }: { shipping_type?: string }) => shipping_type,
      },
      {
        nameKey: `models.order.tracking_number`,
        sortable: false,
        cell: ({ shipments }: { shipments?: unknown }) => {
          if (!Array.isArray(shipments) || shipments.length === 0) {
            return <div></div>
          }
          return (
            <div>
              {shipments.map((shipment: { id?: string; tracking_number?: string } | null, index: number) => {
                if (!shipment || typeof shipment !== 'object') return null
                const trackingNumber = shipment.tracking_number || ''
                return (
                  <div key={shipment.id || index} className="mb-1">
                    {trackingNumber}
                  </div>
                )
              })}
            </div>
          )
        }
      },
      {
        nameKey: `models.order.shipment_status`,
        sortable: false,
        cell: ({ shipments }: { shipments?: unknown }) => {
          if (!Array.isArray(shipments) || shipments.length === 0) {
            return <div></div>
          }
          return (
            <Space direction="vertical" size={2}>
              {shipments.map((shipment: { id?: string; status?: string } | null, index: number) => {
                if (!shipment || typeof shipment !== 'object') return null
                const status = shipment.status || ''
                const displayStatus = shipmentStatusLabel(status)
                const color = SHIPMENT_STATUS_COLORS[status] || 'default'
                return (
                  <Tag key={shipment.id || index} color={color}>
                    {displayStatus}
                  </Tag>
                )
              })}
            </Space>
          )
        }
      },
      {
        name: (
          <Space size={4}>
            {t('models.order.scheduled_shipping_date')}
            <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{t('shared.orders.list.column_tooltips.scheduled_shipping_date')}</span>}>
              <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
            </Tooltip>
          </Space>
        ),
        sortable: true,
        width: '120px',
        sortFieldName: 'orders.scheduled_shipping_date',
        selector: ({ scheduled_shipping_date }: { scheduled_shipping_date?: string }) => scheduled_shipping_date,
      },
      {
        name: (
          <Space size={4}>
            {t('models.order.expected_ship_date')}
            <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{t('shared.orders.list.column_tooltips.expected_ship_date')}</span>}>
              <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
            </Tooltip>
          </Space>
        ),
        sortable: true,
        width: '120px',
        sortFieldName: 'orders.expected_ship_date',
        cell: ({ expected_ship_date, status }: { expected_ship_date?: string; status?: string }) => {
          if (!expected_ship_date) return null;
          const isOverdue = dayjs().isAfter(dayjs(expected_ship_date), 'day') &&
            !['shipped', 'canceled'].includes(status as string);
          return (
            <span style={{ color: isOverdue ? token.colorError : 'inherit', fontWeight: isOverdue ? 'bold' : 'normal' }}>
              {expected_ship_date}
              {isOverdue && (
                <Tooltip title={t('shared.orders.list.overdue_warning')}>
                  <InfoCircleOutlined style={{ marginLeft: 4 }} />
                </Tooltip>
              )}
            </span>
          );
        }
      },
      {
        nameKey: `models.order.delivery_date`,
        sortable: true,
        width: '120px',
        sortFieldName: 'orders.delivery_date',
        selector: ({ delivery_date }: { delivery_date?: string }) => delivery_date,
      },
      {
        nameKey: `models.order.note`,
        sortable: false,
        selector: ({ note }: { note?: string }) => note,
      },
      {
        nameKey: `common.created_at`,
        sortable: true,
        width: '180px',
        sortFieldName: 'orders.created_at',
        cell: ({ created_at }: { created_at?: string }) => formatDateTime(created_at as string),
      },
      {
        name: (
          <Space size={4}>
            {t('models.order.placed_at')}
            <Tooltip title={t('shared.orders.list.column_tooltips.placed_at')}>
              <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
            </Tooltip>
          </Space>
        ),
        sortable: true,
        width: '180px',
        sortFieldName: 'orders.placed_at',
        cell: ({ placed_at }: { placed_at?: string }) => formatDateTime(placed_at as string),
      },
      {
        nameKey: `models.order.shipped_at`,
        sortable: true,
        width: '180px',
        sortFieldName: 'orders.shipped_at',
        cell: ({ shipped_at }: { shipped_at?: string }) => formatDateTime(shipped_at as string),
      }
    );

    return cols;
  }, [role, urlPrefix, merchants, shipmentStatusLabel, token, t, hideMerchantUI])

  const supportRules = useMemo<SupportRule[]>(() => {
    const rules: SupportRule[] = [
      {
        label: t(`shared.orders.list.filters.order_number`),
        component: MultipleValue,
        inputName: 'orders.name',
        defaultPinned: true,
        createable: true,
        tooltip: {
          title: t('shared.orders.list.filter_tooltips.order_number.title'),
          content: t('shared.orders.list.filter_tooltips.order_number.content'),
        },
      },
      {
        label: t(`shared.orders.list.filters.product_sku`),
        component: MultipleValue,
        inputName: 'products.sku',
        createable: true,
        tooltip: {
          title: t('shared.orders.list.filter_tooltips.product_sku.title'),
          content: t('shared.orders.list.filter_tooltips.product_sku.content'),
        },
      },
      {
        label: t(`shared.orders.list.filters.reference_number`),
        component: MultipleValue,
        inputName: 'orders.extra_name',
        createable: true,
        tooltip: {
          title: t('shared.orders.list.filter_tooltips.reference_number.title'),
          content: t('shared.orders.list.filter_tooltips.reference_number.content'),
        },
      }
    ];

    // Add merchant filter for operator role (single-merchant 模式隱藏)
    if (role === 'operator' && !hideMerchantUI) {
      rules.push({
        label: t(`shared.orders.list.filters.merchant`),
        component: MultipleValue,
        inputName: 'orders.merchant_id',
        defaultOptions: merchants.map((merchant) => ({
          label: merchant.name,
          value: merchant.id,
        })),
        tooltip: {
          title: t('shared.orders.list.filter_tooltips.merchant.title'),
          content: t('shared.orders.list.filter_tooltips.merchant.content'),
        },
      });
    }

    rules.push(
      {
        label: t(`common.created_at`),
        component: FullDayRangePicker,

        defaultPinned: true,
        inputName: 'orders.created_at',
        tooltip: {
          title: t('shared.orders.list.filter_tooltips.created_at.title'),
          content: t('shared.orders.list.filter_tooltips.created_at.content'),
        },
      },
      {
        label: t(`models.order.placed_at`),
        component: FullDayRangePicker,

        inputName: 'orders.placed_at',
        tooltip: {
          title: t('shared.orders.list.filter_tooltips.placed_at.title'),
          content: t('shared.orders.list.filter_tooltips.placed_at.content'),
        },
      },
      {
        label: t(`shared.orders.list.filters.shipped_at`),
        component: FullDayRangePicker,

        inputName: 'orders.shipped_at',
        tooltip: {
          title: t('shared.orders.list.filter_tooltips.shipped_at.title'),
          content: t('shared.orders.list.filter_tooltips.shipped_at.content'),
        },
      },
      {
        label: t(`shared.orders.list.filters.delivery_date`),
        component: FullDayRangePicker,

        inputName: 'orders.delivery_date',
        tooltip: {
          title: t('shared.orders.list.filter_tooltips.delivery_date.title'),
          content: t('shared.orders.list.filter_tooltips.delivery_date.content'),
        },
      },
      {
        label: t(`shared.orders.list.filters.scheduled_shipping_date`),
        component: FullDayRangePicker,

        inputName: 'orders.scheduled_shipping_date',
        tooltip: {
          title: t('shared.orders.list.filter_tooltips.scheduled_shipping_date.title'),
          content: t('shared.orders.list.filter_tooltips.scheduled_shipping_date.content'),
        },
      },
      {
        label: t(`shared.orders.list.filters.shipping_type`),
        component: MultipleValue,
        defaultOptions: shippingTypeOptions,
        createable: false,
        inputName: 'orders.shipping_type_id',
        tooltip: {
          title: t('shared.orders.list.filter_tooltips.shipping_type.title'),
          content: t('shared.orders.list.filter_tooltips.shipping_type.content'),
        },
      },
      {
        label: t(`shared.orders.list.filters.tracking_number`),
        component: MultipleValue,
        createable: true,
        inputName: 'shipments.tracking_number',
        tooltip: {
          title: t('shared.orders.list.filter_tooltips.tracking_number.title'),
          content: t('shared.orders.list.filter_tooltips.tracking_number.content'),
        },
      },
      {
        label: t(`shared.orders.list.filters.shipment_status`),
        component: MultipleValue,
        defaultOptions: localizedShipmentStatusOptions,
        createable: false,
        inputName: 'shipments.status',
        tooltip: {
          title: t('shared.orders.list.filter_tooltips.shipment_status.title'),
          content: t('shared.orders.list.filter_tooltips.shipment_status.content'),
        },
      },
      {
        label: t(`shared.orders.list.filters.status`),
        component: MultipleValue,
        defaultOptions: localizedStatusOptions,
        createable: false,
        inputName: 'orders.status',
        tooltip: {
          title: t('shared.orders.list.filter_tooltips.status.title'),
          content: t('shared.orders.list.filter_tooltips.status.content'),
        },
      },
      {
        label: t(`shared.orders.list.filters.receiver_name`),
        component: MultipleValue,
        createable: true,
        inputName: 'orders.receiver_name',
        tooltip: {
          title: t('shared.orders.list.filter_tooltips.receiver_name.title'),
          content: t('shared.orders.list.filter_tooltips.receiver_name.content'),
        },
      },
      {
        label: t(`shared.orders.list.filters.receiver_phone`),
        component: MultipleValue,
        inputName: 'orders.phone',
        createable: true,
        tooltip: {
          title: t('shared.orders.list.filter_tooltips.receiver_phone.title'),
          content: t('shared.orders.list.filter_tooltips.receiver_phone.content'),
        },
      },
      {
        label: t(`shared.orders.list.filters.channel`),
        component: MultipleValue,
        createable: false,
        inputName: 'orders.sales_channel_id',
        defaultOptions: channelOptions,
        tooltip: {
          title: t('shared.orders.list.filter_tooltips.channel.title'),
          content: t('shared.orders.list.filter_tooltips.channel.content'),
        },
      },
      {
        label: t(`shared.orders.list.filters.is_cod`),
        component: BooleanCheckbox,
        inputName: 'orders.is_cod',
        tooltip: {
          title: t('shared.orders.list.filter_tooltips.is_cod.title'),
          content: t('shared.orders.list.filter_tooltips.is_cod.content'),
        },
      },
      {
        label: t(`shared.orders.list.filters.total_amount`),
        component: NumberRange,
        inputName: 'orders.total_amount',
        tooltip: {
          title: t('shared.orders.list.filter_tooltips.total_amount.title'),
          content: t('shared.orders.list.filter_tooltips.total_amount.content'),
        },
      },
      {
        label: t(`shared.orders.list.filters.duplicate_check`),
        component: MultipleValue,
        createable: false,
        inputName: 'orders.duplicate_values',
        defaultOptions: [
          {
            label: t(`shared.orders.list.filters.duplicate_receiver_name`),
            value: 'orders.receiver_name',
          },
          {
            label: t(`shared.orders.list.filters.duplicate_phone`),
            value: 'orders.phone',
          },
          {
            label: t(`shared.orders.list.filters.duplicate_address`),
            value: 'orders.address',
          },
        ],
        tooltip: {
          title: t('shared.orders.list.filter_tooltips.duplicate_check.title'),
          content: t('shared.orders.list.filter_tooltips.duplicate_check.content'),
        },
      },
      {
        label: t(`shared.orders.list.filters.urgent`),
        component: BooleanCheckbox,
        inputName: 'orders.urgent',
        defaultPinned: true,
        tooltip: {
          title: t('shared.orders.list.filter_tooltips.urgent.title'),
          content: t('shared.orders.list.filter_tooltips.urgent.content'),
        },
      },
      {
        label: t(`shared.orders.list.filters.is_held`),
        component: BooleanCheckbox,
        inputName: 'orders.is_held',
        tooltip: {
          title: t('shared.orders.list.filter_tooltips.is_held.title'),
          content: t('shared.orders.list.filter_tooltips.is_held.content'),
        },
      },
      {
        label: t(`shared.orders.list.filters.note_present`),
        component: BooleanCheckbox,
        inputName: 'orders.note_exist',
        tooltip: {
          title: t('shared.orders.list.filter_tooltips.note_present.title'),
          content: t('shared.orders.list.filter_tooltips.note_present.content'),
        },
      },
      {
        label: t(`shared.orders.list.filters.note`),
        component: SearchValue,
        inputName: 'orders.note',
        tooltip: {
          title: t('shared.orders.list.filter_tooltips.note.title'),
          content: t('shared.orders.list.filter_tooltips.note.content'),
        },
      },
      {
        label: t(`shared.orders.list.filters.email`),
        component: MultipleValue,
        inputName: 'orders.email',
        createable: true,
        tooltip: {
          title: t('shared.orders.list.filter_tooltips.email.title'),
          content: t('shared.orders.list.filter_tooltips.email.content'),
        },
      },
    );

    return rules;
  }, [role, localizedShipmentStatusOptions, localizedStatusOptions, shippingTypeOptions, channelOptions, merchants, t, hideMerchantUI])

  // actions runtime shape 包含 folded / group / danger / type 等 TableAction interface 未宣告的欄位，
  // 沿用 .jsx 原本的 loose shape，以 any[] 表示。
  const actions = useMemo<any[]>(() => {
    // Group keys for folded action categorization
    const GROUP_FLOW = 'shared.orders.list.action_groups.flow'
    const GROUP_STATUS = 'shared.orders.list.action_groups.status'
    const GROUP_EXPORT = 'shared.orders.list.action_groups.export'
    const GROUP_MODIFY = 'shared.orders.list.action_groups.modify'

    const baseActions: Array<TableAction & { requiredPermission?: Permission; folded?: boolean; group?: string; danger?: boolean }> = [
      // ── Primary actions (always visible as buttons) ──
      {
        name: `common.button.edit`,
        icon: <EditOutlined />,
        type: 'primary',
        requiredPermission: 'orders:write',
        do: (selected: Iterable<string>) => {
          setBatchEditModalOpen(true)
          setBatchEditModalSelected(selected)
        }
      },
      {
        name: `shared.orders.list.actions.check_stock`,
        icon: <CheckCircleOutlined />,
        requiredPermission: 'orders:write',
        do: (selected: Iterable<string>) => executeBatchOperation({
          task: service ? () => service.batchCheck(Array.from(selected)) : undefined,
          url: 'orders/batch_check.json',
          selected,
          jobName: 'shared.orders.list.actions.check_stock'
        })
      },
      {
        name: `common.button.export`,
        icon: <ExportOutlined />,
        requiredPermission: 'orders:export',
        do: (selected: Iterable<string>) => executeBatchOperation({
          task: service ? () => service.export(Array.from(selected), { locale: i18n.language }) : undefined,
          url: 'orders/export.json',
          selected,
          jobName: 'common.button.export'
        })
      },
      // ── Folded: 流程操作 ──
      {
        name: `shared.orders.list.actions.resume`,
        icon: <ReloadOutlined />,
        folded: true,
        group: GROUP_FLOW,
        requiredPermission: 'orders:write',
        do: (selected: Set<string>) => {
          if (window.confirm(t(`shared.orders.list.confirmations.resume`, { count: selected.size }))) {
            executeBatchOperation({
              task: service ? () => service.batchResume(Array.from(selected)) : undefined,
              url: 'orders/batch_resume.json',
              selected,
              jobName: 'shared.orders.list.actions.resume',
              onSuccess: () => setLastModified(Date.now())
            })
          }
        }
      },
      {
        name: `shared.orders.list.actions.hold`,
        icon: <StopOutlined />,
        folded: true,
        group: GROUP_FLOW,
        requiredPermission: 'orders:write',
        do: (selected: Set<string>) => {
          setBatchHoldModalOpen(true)
          setBatchHoldSelected(selected)
        }
      },
      {
        name: `common.button.cancel`,
        icon: <CloseCircleOutlined />,
        danger: true,
        folded: true,
        group: GROUP_FLOW,
        requiredPermission: 'orders:write',
        do: (selected: Set<string>) => {
          setBatchCancelModalOpen(true)
          setBatchCancelSelected(selected)
        }
      },
      {
        name: `shared.orders.list.actions.create_return`,
        icon: <RollbackOutlined />,
        folded: true,
        group: GROUP_FLOW,
        requiredPermission: 'orders:write',
        do: (selected: Set<string>) => {
          if (window.confirm(t(`shared.orders.list.confirmations.create_return`, { count: selected.size }))) {
            executeBatchOperation({
              task: service ? () => service.batchCreateReturnOrder(Array.from(selected)) : undefined,
              url: 'orders/batch_create_return_order.json',
              selected,
              jobName: 'shared.orders.list.actions.create_return',
              onSuccess: () => setLastModified(Date.now())
            })
          }
        }
      },
      // ── Folded: 狀態轉換 ──
      {
        name: `shared.orders.list.actions.draft_to_normal`,
        icon: <SwapOutlined />,
        folded: true,
        group: GROUP_STATUS,
        requiredPermission: 'orders:write',
        do: (selected: Set<string>) => {
          if (window.confirm(t(`shared.orders.list.confirmations.draft_to_normal`, { count: selected.size }))) {
            executeBatchOperation({
              task: service ? () => service.batchDraftOrderConvert(Array.from(selected)) : undefined,
              url: 'orders/batch_draft_order_convert.json',
              selected,
              jobName: 'shared.orders.list.actions.draft_to_normal',
              onSuccess: () => setLastModified(Date.now())
            })
          }
        }
      },
      {
        name: `shared.orders.list.actions.normal_to_draft`,
        icon: <FileAddOutlined />,
        folded: true,
        group: GROUP_STATUS,
        requiredPermission: 'orders:write',
        do: (selected: Iterable<string>) => {
          setIsDraftOrderModalOpen(true)
          setDraftOrderModalSelected(selected)
        }
      },
      {
        name: `shared.orders.list.actions.normal_to_urgent`,
        icon: <ThunderboltOutlined />,
        folded: true,
        group: GROUP_STATUS,
        requiredPermission: 'orders:write',
        do: (selected: Set<string>) => {
          if (window.confirm(t(`shared.orders.list.confirmations.normal_to_urgent`, { count: selected.size }))) {
            executeBatchOperation({
              task: service ? () => service.batchConvertToUrgentOrder(Array.from(selected)) : undefined,
              url: 'orders/batch_convert_to_urgent_order.json',
              selected,
              jobName: 'shared.orders.list.actions.normal_to_urgent',
              onSuccess: () => setLastModified(Date.now())
            })
          }
        }
      },
      {
        name: `shared.orders.list.actions.urgent_to_normal`,
        icon: <SwapOutlined />,
        folded: true,
        group: GROUP_STATUS,
        requiredPermission: 'orders:write',
        do: (selected: Set<string>) => {
          if (window.confirm(t(`shared.orders.list.confirmations.urgent_to_normal`, { count: selected.size }))) {
            executeBatchOperation({
              task: service ? () => service.batchUrgentOrderConvert(Array.from(selected)) : undefined,
              url: 'orders/batch_urgent_order_convert.json',
              selected,
              jobName: 'shared.orders.list.actions.urgent_to_normal',
              onSuccess: () => setLastModified(Date.now())
            })
          }
        }
      },
      // ── Folded: 匯出/文件 ──
      {
        name: `shared.orders.list.actions.download_pdf`,
        icon: <FilePdfOutlined />,
        folded: true,
        group: GROUP_EXPORT,
        requiredPermission: 'orders:write',
        do: (selected: Iterable<string>) => executeBatchOperation({
          task: service ? () => service.downloadPdf(Array.from(selected)) : undefined,
          url: 'orders/download_pdf.json',
          selected,
          jobName: 'shared.orders.list.actions.download_pdf'
        })
      },
      {
        name: `shared.orders.list.actions.export_with_allocation`,
        icon: <ExportOutlined />,
        folded: true,
        group: GROUP_EXPORT,
        requiredPermission: 'orders:export',
        do: (selected: Iterable<string>) => executeBatchOperation({
          task: service ? () => service.export(Array.from(selected), { export_order_inventories: true, locale: i18n.language }) : undefined,
          url: 'orders/export.json',
          selected,
          params: { export_order_inventories: true, locale: i18n.language },
          jobName: 'shared.orders.list.actions.export_with_allocation'
        })
      },
      // 拆分出貨單（operator 限定）— 開 OrderBatchPanel 讓使用者選拆分模式（缺貨 / 指定 SKU）
      // backend 用 BatchSplitOrdersWorker，金額由 AmountSplitter 按比例守恆分配
      ...(role === 'operator' ? [
        {
          name: `shared.orders.list.actions.split`,
          icon: <ScissorOutlined />,
          folded: true,
          group: GROUP_MODIFY,
          requiredPermission: 'orders:write' as const,
          do: () => {
            setShowBatchEditPanel(true)
          }
        },
      ] : []),
    ];

    // 暫時隱藏的批次操作（保留邏輯，只是不顯示在「更多操作」選單）
    const HIDDEN_ACTION_NAMES = new Set([
      'shared.orders.list.actions.draft_to_normal',
      'shared.orders.list.actions.normal_to_draft',
    ])
    return baseActions
      .filter(item => !HIDDEN_ACTION_NAMES.has(item.name))
      .filter(item => !item.requiredPermission || can(item.requiredPermission));
  }, [
    role,
    t,
    can,
    executeBatchOperation,
    service, // Add service to dependency
    setLastModified,
    setBatchEditModalOpen,
    setBatchEditModalSelected,
    setShowBatchEditPanel,
    setIsDraftOrderModalOpen,
    setDraftOrderModalSelected,
    setBatchHoldModalOpen,
    setBatchHoldSelected,
    i18n
  ])

  return (
    <>
      <TableIndex
        columns={baseColumns}
        searchable={{ match: 'prefix', searchFields: [t('models.order.order_number'), t('models.order.reference_number'), t('models.order.receiver_name')] }}
        columnsEditable={true}
        tableId={`${role === 'operator' ? 'Operator' : 'Admin'}OrdersTable`}
        defaultQuery={{
          sorting_field: 'id',
          sorting_direction: 'desc',
        }}
        showPanel={showBatchEditPanel}
        onClosePanel={onCloseBatchEditPanel}
        supportRules={supportRules}
        lastModified={lastModified}
        setLastModified={setLastModified}
        actions={actions}
        service={service}
        filterHelpExample={t('shared.orders.list.filter_help_example') as string}
        filterDocsUrl={`/docs/${role}/orders/list#features`}
        batchEditPanel={{
          render: ({ selected, showPanel, onClose, query, setLastModified }: { selected: unknown; showPanel: boolean; onClose: () => void; query: Record<string, unknown>; setLastModified: (v: number) => void }): ReactNode => (
            <OrderBatchPanel
              showPanel={showPanel}
              onClosePanel={onClose}
              selected={selected as Set<string>}
              query={query as Record<string, unknown>}
              setLastModified={setLastModified}
              role={role}
              service={service} // Pass service to OrderBatchPanel
              addJob={addJob}
            />
          )
        }}
      />
      <ConvertOrderToDraftOrderModal
        open={isDraftOrderModalOpen}
        onClose={() => { setIsDraftOrderModalOpen(false) }}
        selected={draftOrderModalSelected}
        onTaskDone={() => {
          setLastModified(Date.now())
        }}
        role={role}
        service={service}
        addJob={addJob}
      />
      <BatchEditModal
        open={batchEditModalOpen}
        channelOptions={channelOptions}
        shippingTypes={shippingTypes}
        onClose={() => { setBatchEditModalOpen(false) }}
        selected={batchEditModalSelected}
        onTaskDone={() => {
          setLastModified(Date.now())
        }}
        service={service}
        addJob={addJob}
      />
      <ConfirmModal
        open={batchCancelModalOpen}
        onClose={() => { setBatchCancelModalOpen(false) }}
        onConfirm={() => {
          executeBatchOperation({
            task: service ? () => service.batchCancel(Array.from(batchCancelSelected)) : undefined,
            url: 'orders/batch_cancel.json',
            selected: batchCancelSelected,
            jobName: 'common.button.cancel',
            onSuccess: () => setLastModified(Date.now())
          })
          setBatchCancelModalOpen(false)
        }}
        title={t(`shared.orders.list.batch_cancel_confirm.title`)}
        description={t(`shared.orders.list.batch_cancel_confirm.warning`, { count: batchCancelSelected.size })}
        confirmText="cancel"
        danger={true}
      />
      <ImportModal
        open={importModalVisible}
        onClose={() => setImportModalVisible(false)}
        onImport={handleModalImport}
        onDownloadTemplate={handleDownloadTemplate}
        role={role}
        merchants={merchants}
        title={t('shared.orders.list.import.modal_title')}
        importTypeOptions={importTypeOptions}
        defaultImportType={defaultImportType}
        importTypeLabel={t(`models.order.order_type`)}
        allowAttachments={true}
      />
      <HoldOrderModal
        visible={batchHoldModalOpen}
        onCancel={() => { setBatchHoldModalOpen(false) }}
        onConfirm={(values: { reason: string; note?: string }) => {
          executeBatchOperation({
            task: service ? () => service.batchHold(Array.from(batchHoldSelected), values.reason, values.note) : undefined,
            url: 'orders/batch_hold.json',
            selected: batchHoldSelected,
            params: { reason: values.reason, note: values.note },
            jobName: 'shared.orders.list.actions.hold',
            onSuccess: () => setLastModified(Date.now())
          })
          setBatchHoldModalOpen(false)
        }}
        orderName={t('shared.orders.list.batch_hold_confirm.order_count', { count: batchHoldSelected.size || 0 })}
      />
    </>
  );
}

export default OrderList
